import React, { useMemo } from 'react';

// Seeded random number generator for deterministic results
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Constant seed for deterministic star generation
const STAR_SEED = 42;

export default function StarryBackground() {
  // Generate random stars with deterministic values
  const stars = useMemo(() => {
    const starArray = [];
    const starCount = 150;
    
    for (let i = 0; i < starCount; i++) {
      const seed = STAR_SEED + i;
      starArray.push({
        id: i,
        left: `${seededRandom(seed) * 100}%`,
        top: `${seededRandom(seed + 1000) * 100}%`,
        size: seededRandom(seed + 2000) * 3 + 1,
        delay: seededRandom(seed + 3000) * 3,
        duration: seededRandom(seed + 4000) * 2 + 2,
        opacity: seededRandom(seed + 5000) * 0.5 + 0.3,
      });
    }
    return starArray;
  }, []);

  // Generate shooting stars with deterministic values
  const shootingStars = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => {
      const seed = STAR_SEED + i + 10000;
      return {
        id: i,
        top: `${seededRandom(seed) * 50}%`,
        left: `${seededRandom(seed + 100) * 100}%`,
        delay: i * 5 + seededRandom(seed + 200) * 3,
      };
    });
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Static gradient background */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 20%, hsl(265 72% 58% / 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, hsl(185 100% 50% / 0.1) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, hsl(300 70% 60% / 0.08) 0%, transparent 60%)
          `
        }}
      />

      {/* Twinkling stars */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-foreground"
          style={{
            left: star.left,
            top: star.top,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
          }}
        />
      ))}

      {/* Shooting stars */}
      {shootingStars.map((star) => (
        <div
          key={`shooting-${star.id}`}
          className="absolute"
          style={{
            top: star.top,
            left: star.left,
            animation: `shootingStar 3s linear ${star.delay}s infinite`,
          }}
        >
          <div 
            className="w-1 h-1 rounded-full"
            style={{
              background: 'linear-gradient(to right, hsl(var(--foreground)), transparent)',
              boxShadow: '0 0 6px 2px hsl(var(--foreground) / 0.5)',
              transform: 'rotate(-45deg)',
              width: '80px',
              height: '2px',
            }}
          />
        </div>
      ))}

      {/* Large glowing orbs for depth */}
      <div 
        className="absolute w-96 h-96 rounded-full blur-3xl animate-float-slow"
        style={{
          background: 'radial-gradient(circle, hsl(265 72% 58% / 0.2) 0%, transparent 70%)',
          top: '10%',
          left: '5%',
        }}
      />
      <div 
        className="absolute w-80 h-80 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, hsl(185 100% 50% / 0.15) 0%, transparent 70%)',
          bottom: '20%',
          right: '10%',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      {/* Shooting star keyframes injection */}
      <style>{`
        @keyframes shootingStar {
          0% {
            transform: translateX(0) translateY(0) rotate(-45deg);
            opacity: 1;
          }
          70% {
            opacity: 1;
          }
          100% {
            transform: translateX(-500px) translateY(500px) rotate(-45deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
